import React from 'react';
import { Box, Link, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { BRAND_SANS_FONT, brandConfig } from '../../shared/design/theme';
import { LandingContainer } from '../home/HomePage.styles';
import '../home/HomePage.styles.css';
import { Footer } from '../../shared/components/Footer';
import SkipLink from '../../shared/components/SkipLink';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';

const ACCENT = '#B83B1D';
const INK = '#2E140A';

const FOCUS_RING = {
  '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 3, borderRadius: '4px' },
};

// Kept as a named export for backward compatibility: some pages import
// `{ linkStyle }` to style inline links. It mirrors the in-content `& a` rule.
export const linkStyle = {
  color: ACCENT,
  textDecorationColor: alpha(ACCENT, 0.4),
  textUnderlineOffset: '0.18em',
  fontWeight: 500,
  '&:hover': { textDecorationColor: ACCENT },
  ...FOCUS_RING,
};

/**
 * Clean document layout for static pages (Privacy, Terms, and any other prose
 * page). No card/box, no ambient glows, no "Back to Home" button (the logo is
 * the home link). Same visual language as the /security page.
 *
 * Typography here is tuned for long-form reading rather than for fitting the
 * document on one screen: the title outranks the section headings by a clear
 * step, body copy sits at 0.9rem, and the prose column is capped by measure
 * (ch) rather than by pixel width, so a line stays inside the range an eye can
 * track without losing its place.
 *
 * These pages do not render SiteHeader, so the skip link comes from here.
 */
export default function LegalPageLayout({
  eyebrow,
  title,
  subtitle,
  effectiveDate,
  // [{ id, label }] for the on-this-page nav. Rendered between the intro and
  // the prose, outside the prose container so it does not inherit the inline
  // link and bullet styling that governs the document body.
  sectionLinks = null,
  children,
}) {
  const hair = alpha(INK, 0.08);

  return (
    <LandingContainer data-theme="light" sx={{ minHeight: '100vh' }}>
      <SkipLink />

      {/* Header: logo only (logo is the home link) + a single demo link.
          No box, no back button, no center sub-nav. */}
      <Box component="header" sx={{ borderBottom: `1px solid ${hair}` }}>
        <Box
          sx={{
            maxWidth: 960,
            mx: 'auto',
            px: { xs: 2.5, md: 4 },
            py: { xs: 1.75, md: 2 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box
            component="a"
            href="/"
            aria-label="Restack home"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              textDecoration: 'none',
              ...FOCUS_RING,
            }}
          >
            <brandConfig.icon aria-hidden="true" sx={{ fontSize: 22, color: 'text.primary' }} />
            <Box
              component="span"
              sx={{
                fontFamily: BRAND_SANS_FONT,
                fontWeight: 600,
                fontSize: '1rem',
                letterSpacing: '-0.02em',
                color: 'text.primary',
              }}
            >
              Restack
            </Box>
          </Box>
          <Link
            href="/demo"
            sx={{
              ...linkStyle,
              fontFamily: BRAND_SANS_FONT,
              fontSize: '0.82rem',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.4,
            }}
          >
            See it on your own deck
            <ArrowForwardRounded aria-hidden="true" sx={{ fontSize: '0.95rem' }} />
          </Link>
        </Box>
      </Box>

      {/* Content: centered reading column at a comfortable measure. */}
      <Box
        component="main"
        id="main-content"
        sx={{
          maxWidth: 820,
          mx: 'auto',
          px: { xs: 2.5, md: 4 },
          pt: { xs: 4, md: 5.5 },
          pb: { xs: 6, md: 9 },
          '&:focus': { outline: 'none' },
        }}
      >
        {/* Intro */}
        <Typography
          component="div"
          sx={{
            fontFamily: BRAND_SANS_FONT,
            fontSize: '0.68rem',
            fontWeight: 600,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: ACCENT,
          }}
        >
          {eyebrow}
        </Typography>
        <Typography
          component="h1"
          sx={{
            mt: 1.25,
            mb: 1.25,
            fontFamily: BRAND_SANS_FONT,
            // A clear step above the section headings below (1.05rem), so the
            // document reads as title then sections rather than as one flat
            // stack of near-identical lines.
            fontSize: { xs: '1.6rem', md: '1.95rem' },
            fontWeight: 600,
            letterSpacing: '-0.025em',
            lineHeight: 1.18,
            color: 'text.primary',
            textWrap: 'balance',
          }}
        >
          {title}
        </Typography>
        <Typography
          component="div"
          sx={{
            fontFamily: BRAND_SANS_FONT,
            fontSize: { xs: '0.9rem', md: '0.95rem' },
            fontWeight: 400,
            lineHeight: 1.65,
            color: 'text.secondary',
            maxWidth: '68ch',
            textWrap: 'pretty',
          }}
        >
          {subtitle}
        </Typography>
        {effectiveDate && (
          <Typography
            component="div"
            sx={{
              mt: 1.5,
              fontFamily: BRAND_SANS_FONT,
              fontSize: '0.72rem',
              fontWeight: 500,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: alpha(INK, 0.45),
            }}
          >
            Effective date: {effectiveDate}
          </Typography>
        )}

        {/* On this page. A legal document is read by section, not top to
            bottom, so give it a way in. Same anchor-pill pattern the Trust
            Center and Company pages use. */}
        {sectionLinks?.length ? (
          <Box component="nav" aria-label="On this page" sx={{ mt: { xs: 3, md: 3.5 } }}>
            <Typography
              component="div"
              sx={{
                fontFamily: BRAND_SANS_FONT,
                fontSize: '0.68rem',
                fontWeight: 600,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: alpha(INK, 0.45),
                mb: 1.25,
              }}
            >
              On this page
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {sectionLinks.map((s) => (
                <Box
                  key={s.id}
                  component="a"
                  href={`#${s.id}`}
                  sx={{
                    fontFamily: BRAND_SANS_FONT,
                    fontSize: '0.78rem',
                    fontWeight: 500,
                    color: 'text.secondary',
                    textDecoration: 'none',
                    px: 1.4,
                    py: 0.55,
                    borderRadius: '999px',
                    border: `1px solid ${hair}`,
                    transition: 'color 0.2s ease, border-color 0.2s ease, background-color 0.2s ease, transform 0.12s ease',
                    '&:hover': { color: ACCENT, borderColor: alpha(ACCENT, 0.3), backgroundColor: alpha(ACCENT, 0.04) },
                    '&:active': { transform: 'translateY(1px)' },
                    ...FOCUS_RING,
                    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                  }}
                >
                  {s.label}
                </Box>
              ))}
            </Box>
          </Box>
        ) : null}

        {/* Prose content. The descendant rules style whatever the page passes
            (headings, paragraphs, lists, inline links, code). */}
        <Box
          className="legal-content-area"
          sx={{
            mt: { xs: 3.5, md: 4.5 },
            display: 'flex',
            flexDirection: 'column',
            // One rhythm: 16px between prose blocks, 40px before a new
            // section heading (the h2 adds its own top margin on top of the
            // gap). Nothing in the page sets a one-off margin.
            gap: 2,
            '& h2': {
              fontFamily: BRAND_SANS_FONT,
              fontSize: { xs: '1.05rem', md: '1.1rem' },
              fontWeight: 600,
              letterSpacing: '-0.015em',
              lineHeight: 1.3,
              color: 'text.primary',
              mt: 3,
              mb: 0,
              // Anchor jumps from the on-this-page nav land with the heading
              // clear of the top edge rather than flush against it.
              scrollMarginTop: '24px',
            },
            '& h3': {
              fontFamily: BRAND_SANS_FONT,
              fontSize: '0.95rem',
              fontWeight: 600,
              color: 'text.primary',
              mt: 1.5,
              mb: 0,
              scrollMarginTop: '24px',
            },
            '& p': {
              fontFamily: BRAND_SANS_FONT,
              fontSize: '0.9rem',
              fontWeight: 400,
              lineHeight: 1.75,
              color: 'text.secondary',
              m: 0,
              maxWidth: '72ch',
              textWrap: 'pretty',
            },
            '& ul': {
              listStyle: 'none',
              m: 0,
              p: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              maxWidth: '72ch',
            },
            '& li': {
              position: 'relative',
              pl: '1.05rem',
              fontFamily: BRAND_SANS_FONT,
              fontSize: '0.9rem',
              lineHeight: 1.7,
              fontWeight: 400,
              color: 'text.secondary',
              textWrap: 'pretty',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: '0.6em',
                width: 5,
                height: 5,
                borderRadius: '50%',
                backgroundColor: ACCENT,
              },
            },
            '& strong': { color: 'text.primary', fontWeight: 600 },
            '& code': {
              fontFamily:
                'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
              fontSize: '0.8em',
              backgroundColor: alpha(INK, 0.05),
              padding: '0.05em 0.35em',
              borderRadius: '4px',
            },
            '& a': linkStyle,
          }}
        >
          {children}
        </Box>
      </Box>

      <Footer />
    </LandingContainer>
  );
}
